import { produce, castDraft } from 'immer';
import type { Draft } from 'immer';

import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';

import type { ReducerContext } from '../types/reducer-context.type';

/**
 * Runs `mutate` against a draft of `state`, historizing the previous version
 * and bumping `currentVersion`.
 *
 * `mutate` may return `false` to signal that nothing actually changed, in
 * which case the original `state` is returned untouched (no history entry,
 * no version bump). Any other return value is ignored and the change is
 * committed.
 */
export const commitVersionedChange = <E extends Editable>(
  state: DocumentEditorState<E>,
  context: Readonly<Pick<ReducerContext<E>, 'pushInPast'>>,
  mutate: (draft: Draft<DocumentEditorState<E>>) => unknown,
): DocumentEditorState<E> => {
  const newPastVersions = context.pushInPast(state);

  return produce(state, (draft) => {
    const hasChanged = mutate(draft);

    if (hasChanged === false) {
      return state as Draft<DocumentEditorState<E>>;
    }

    draft.pastVersions = castDraft(newPastVersions);
    draft.futureVersions = [];
    draft.currentVersion = state.currentVersion + 1;

    return undefined;
  });
};
